import React, { useEffect, useMemo, useRef, useState } from "react";
import { getFileIcon } from "@pages/Project/components/Tabs/FileUploadTab";
import { ServerFile } from "@shared/interfaces/ProjectsFiles";
import { Option } from "./AssistantForm";
import { gdriveService } from "@services/GdriveService";
import { DriveFolder } from "@shared/interfaces/CloudFiles";
import DriveFolderDropdownPreview from "./DrivefolderDropdownPreview";
import useGDrive from "@pages/Project/components/Tabs/components/Files/useGDrive";
import { ExternalFileData } from "@pages/Project/components/Tabs/components/Files/ExternalFilePreview";
import useOneDrive from "@pages/Project/components/Tabs/components/Files/useOneDrive";
import { LocalFolder } from "@pages/Project/components/Tabs/components/Files/LocalFilesFolders";

/** Local FolderView shape (files are mapped to ServerFile-like below) */
type FolderView = {
  parent_id: number | null;
  folders: LocalFolder[];
  files: ServerFile[];
  breadcrumbs: { id: number; name: string }[];
};

interface MultiSelectDropdownProps {
  selected: Option[];
  onChange: (selected: Option[]) => void;
  placeholder: string;
  disabled?: boolean;
  showOneLine?: boolean;
  selectedProject?: Option;
}

const SECTION_HEADER_CLASSES =
  "px-3 py-2 text-[11px] 2xl:text-xs font-semibold text-gray-500 bg-gray-50 sticky top-0 z-10 flex justify-between items-center cursor-pointer select-none";

const ROW_BASE_CLASSES =
  "px-3 py-2.5 flex items-center cursor-pointer text-gray-900 text-xs 2xl:text-sm hover:bg-[#F2F4F7]";

const ArrowIcon: React.FC<{ isOpen: boolean }> = ({ isOpen }) => (
  <img
    src="/arrow-down.svg"
    className={`w-4 h-4 transform transition-transform duration-200 ${
      isOpen ? "rotate-180" : ""
    }`}
    alt="Toggle section"
  />
);

// Be defensive about folder naming differences
const getFolderName = (f: Partial<LocalFolder> | Partial<DriveFolder>) =>
  (f as any).name ?? (f as any).folder_name ?? "Untitled folder";

/** ─────────────────────────────────────────────
 *  Inline recursive Local Folder Node (lazy load)
 *  ───────────────────────────────────────────── */
const LocalFolderNode: React.FC<{
  folder: LocalFolder;
  projectId: string;
  isSelected: (id: string) => boolean;
  onToggle: (file: ServerFile) => void;
  showOneLine: boolean;
  depth?: number;
}> = ({ folder, projectId, isSelected, onToggle, showOneLine, depth = 0 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<ServerFile[]>([]);
  const [subfolders, setSubfolders] = useState<LocalFolder[]>([]);

  const fetchFolderView = async () => {
    setLoading(true);
    try {
      const { localFolderService } = await import(
        "@services/LocalFolderService"
      );
      // pagesize = 100 as requested
      const res = await localFolderService.getFolderView(
        projectId,
        Number(folder.id),
        1,
        100
      );
      setFiles(res.files.filter((f: ServerFile) => f.status === "ready"));
      setSubfolders(res.folders ?? []);
    } catch (e) {
      console.error("Error loading local folder view:", e);
      setFiles([]);
      setSubfolders([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleOpen = async () => {
    if (!isOpen && files.length === 0 && subfolders.length === 0) {
      await fetchFolderView();
    }
    setIsOpen((prev) => !prev);
  };

  // use inline style for padding to avoid Tailwind purge on dynamic classes
  const indentStyle = { paddingLeft: `${Math.min(depth, 6) * 12}px` };

  return (
    <>
      <div
        className={SECTION_HEADER_CLASSES}
        style={indentStyle}
        onClick={toggleOpen}
        aria-expanded={isOpen}
      >
        <div className="flex items-center space-x-1 px-3">
          <img src="/folder.svg" alt="" className="h-4 w-4" />
          <span>{getFolderName(folder)}</span>
        </div>
        <ArrowIcon isOpen={isOpen} />
      </div>

      {isOpen && (
        <>
          {loading && (
            <div
              className="px-3 py-2 text-xs text-gray-400"
              style={indentStyle}
            >
              Loading…
            </div>
          )}

          {files.map((file) => {
            const checked = isSelected(file.id);
            return (
              <div
                key={`local-file-${file.id}`}
                onClick={() => onToggle(file)}
                className={`${ROW_BASE_CLASSES} ${
                  checked ? "bg-[#F2F4F7]" : ""
                }`}
                style={indentStyle}
              >
                <input
                  type="checkbox"
                  className="mr-2 accent-blue-600"
                  checked={checked}
                  onChange={() => onToggle(file)}
                  onClick={(e) => e.stopPropagation()}
                />
                <img
                  src={getFileIcon(file.filetype)}
                  alt=""
                  className="h-5 w-5 mr-2"
                />
                <p
                  className={
                    showOneLine
                      ? "text-ellipsis line-clamp-1"
                      : "break-all whitespace-normal"
                  }
                >
                  {file.filename}
                </p>
              </div>
            );
          })}

          {subfolders.map((sf) => (
            <LocalFolderNode
              key={`local-folder-${sf.id}`}
              folder={sf}
              projectId={projectId}
              isSelected={isSelected}
              onToggle={onToggle}
              showOneLine={showOneLine}
              depth={depth + 1}
            />
          ))}

          {!loading && files.length === 0 && subfolders.length === 0 && (
            <div className=" py-2 text-xs text-gray-400" style={indentStyle}>
              <span className="pl-3">Empty folder</span>
            </div>
          )}
        </>
      )}
    </>
  );
};

const MultiSelectDropdownAssistant: React.FC<MultiSelectDropdownProps> = ({
  selected,
  onChange,
  placeholder,
  disabled = false,
  showOneLine = true,
  selectedProject,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Local FolderView (root)
  const [rootView, setRootView] = useState<FolderView | null>(null);
  const [loadingRootView, setLoadingRootView] = useState(false);
  const [localFilesOpen, setLocalFilesOpen] = useState(true);
  const [localFoldersOpen, setLocalFoldersOpen] = useState(true);

  // GDrive
  const [externalFiles, setExternalFiles] = useState<ExternalFileData>({
    last_sync: { google: "" },
    total_count: 0,
    files: [],
  });
  const [loadingExternal, setLoadingExternal] = useState(false);
  const [gdriveFolders, setGdriveFolders] = useState<DriveFolder[]>([]);
  const [foldersOpen, setFoldersOpen] = useState(true);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [externalOpen, setExternalOpen] = useState(true);

  // OneDrive
  const [externalFilesDataOneDrive, setExternalFilesDataOneDrive] =
    useState<ExternalFileData>({
      last_sync: { google: "" },
      total_count: 0,
      files: [],
    });
  const [loadingExternalFilesOneDrive, setLoadingExternalFilesOneDrive] =
    useState(false);
  const [oneDriveFolders, setOneDriveFolders] = useState<DriveFolder[]>([]);
  const [foldersOpenOneDrive, setFoldersOpenOneDrive] = useState(true);
  const [loadingFoldersOneDrive, setLoadingFoldersOneDrive] = useState(false);
  const [externalOpenOneDrive, setExternalOpenOneDrive] = useState(true);

  // Load Local root FolderView (folderId = 0, pagesize = 100)
  const getRootFolderView = async () => {
    if (!selectedProject?.id) return;
    try {
      setLoadingRootView(true);
      const { localFolderService } = await import(
        "@services/LocalFolderService"
      );
      const res = await localFolderService.getFolderView(
        String(selectedProject.id),
        0,
        1,
        100
      );
      setRootView({
        parent_id: res.parent_id ?? null,
        folders: res.folders ?? [],
        files: res.files.filter((f: ServerFile) => f.status === "ready"),
        breadcrumbs: res.breadcrumbs ?? [],
      });
    } catch (e) {
      console.error("Error fetching root FolderView:", e);
      setRootView({ parent_id: null, folders: [], files: [], breadcrumbs: [] });
    } finally {
      setLoadingRootView(false);
    }
  };

  // Hooks for cloud providers
  const { fetchFolders, loadExternalFiles, fetchFolderFiles } = useGDrive({
    projectId: selectedProject?.id,
    setGDriveFoldersLoading: setLoadingFolders,
    setFolders: setGdriveFolders,
    setExternalFilesData: setExternalFiles,
    setLoadingExternalFiles: setLoadingExternal,
    gdriveService,
  });

  const {
    fetchFoldersOneDrive,
    loadExternalFilesOneDrive,
    fetchOneDriveFolderFiles,
  } = useOneDrive({
    projectId: selectedProject?.id,
    setOneDriveFolders,
    setOneDriveFoldersLoading: setLoadingFoldersOneDrive,
    setGDriveFoldersLoading: setLoadingFoldersOneDrive,
    setExternalFilesDataOneDrive,
    setLoadingExternalFilesOneDrive,
  });

  // Initial load / on project change
  useEffect(() => {
    if (!selectedProject?.id || selectedProject.id === "0") return;

    getRootFolderView();
    loadExternalFiles();
    fetchFolders();
    loadExternalFilesOneDrive();
    fetchFoldersOneDrive();
  }, [selectedProject?.id]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const isSelected = (id: string) => selected.some((s) => s.id === id);

  const handleToggle = (file: ServerFile) => {
    const exists = isSelected(file.id);
    if (exists) {
      onChange(selected.filter((s) => s.id !== file.id));
    } else {
      onChange([
        ...selected,
        { id: file.id, name: file.filename, icon: file.filetype },
      ]);
    }
  };

  const selectedLabel = useMemo(() => {
    if (selected.length === 0) return placeholder;
    if (selected.length === 1) return selected[0].name;
    return `Documents (${selected.length})`;
  }, [placeholder, selected]);

  const selectedSingleFileType = useMemo(() => {
    if (selected.length !== 1) return "";
    return selected[0].icon || getFileIcon("genericfile");
  }, [selected, rootView, externalFiles, externalFilesDataOneDrive]);

  const localFilesCount = rootView?.files?.length ?? 0;
  const localFoldersCount = rootView?.folders?.length ?? 0;

  const allEmpty =
    localFilesCount === 0 &&
    localFoldersCount === 0 &&
    !externalFiles.files.length &&
    !gdriveFolders.length &&
    !externalFilesDataOneDrive.files.length &&
    !oneDriveFolders.length;

  const buttonDisabled = disabled || allEmpty;

  return (
    <div className="relative flex-1 flex" ref={dropdownRef}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className=" bg-[#FFFFFE] flex-1 border border-[#E4E7EC] text-gray-900 flex justify-between items-center space-x-1.5 font-medium p-2.5 rounded-md text-sm focus:outline-none disabled:text-gray-400 disabled:cursor-not-allowed"
        onClick={() => setIsOpen((prev) => !prev)}
        disabled={buttonDisabled}
      >
        <div className="flex flex-1 space-x-1 items-center">
          {selected.length === 1 ? (
            <img
              src={getFileIcon(selectedSingleFileType)}
              alt=""
              className="h-5 w-5 2xl:h-6 2xl:w-6"
            />
          ) : (
            <img
              src="/file-text.svg"
              alt="File icon"
              className="h-5 w-5 2xl:h-6 2xl:w-6"
            />
          )}
          <p className="break-all text-ellipsis line-clamp-1 text-left text-xs 2xl:text-sm">
            {selectedLabel}
          </p>
        </div>
        <img src="/arrow-down.svg" className="w-5" alt="Dropdown arrow" />
      </button>

      {isOpen && (
        <div
          role="listbox"
          className="absolute mt-1 w-52 2xl:w-64 bg-white rounded-md overflow-hidden shadow-lg z-50"
        >
          <div className="max-h-64 overflow-y-auto scrollbar-hide">
            {/* Local Files (root.files, flat) */}
            {!loadingRootView && (
              <>
                {
                  <div
                    className={SECTION_HEADER_CLASSES}
                    onClick={() => setLocalFilesOpen((prev) => !prev)}
                  >
                    <div className="flex items-center space-x-1">
                      <img
                        src="/folder.svg"
                        alt="Local files icon"
                        className="h-4 w-4"
                      />
                      <span>
                        Local Documents{loadingRootView && " — Loading…"}
                      </span>
                    </div>
                    <ArrowIcon isOpen={localFilesOpen} />
                  </div>
                }
                {localFilesOpen && (
                  <>
                    {(rootView?.files ?? []).map((file) => {
                      const checked = isSelected(file.id);
                      return (
                        <div
                          key={`local-root-file-${file.id}`}
                          onClick={() => handleToggle(file)}
                          className={`${ROW_BASE_CLASSES} ${
                            checked ? "bg-[#F2F4F7]" : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="mr-2 accent-blue-600"
                            checked={checked}
                            onChange={() => handleToggle(file)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <img
                            src={getFileIcon(file.filetype)}
                            alt=""
                            className="h-5 w-5 mr-2"
                          />
                          <p
                            className={
                              showOneLine
                                ? "text-ellipsis line-clamp-1"
                                : "break-all whitespace-normal"
                            }
                          >
                            {file.filename}
                          </p>
                        </div>
                      );
                    })}

                    {/* Local Folders (root.folders -> recursive, lazy) */}
                    {localFoldersCount > 0 && (
                      <>
                        {(rootView?.folders ?? []).map((folder) => (
                          <LocalFolderNode
                            key={`root-folder-${folder.id}`}
                            folder={folder}
                            projectId={String(selectedProject?.id || "")}
                            isSelected={isSelected}
                            onToggle={handleToggle}
                            showOneLine={showOneLine}
                            depth={0}
                          />
                        ))}
                      </>
                    )}

                    {localFilesCount === 0 && localFoldersCount === 0 && (
                      <div className="px-3 py-2 text-xs text-gray-400">
                        No local documents
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* Google Drive External Files */}
            {(loadingExternal || externalFiles.files.length > 0) && (
              <>
                <div
                  className={SECTION_HEADER_CLASSES}
                  onClick={() => setExternalOpen((prev) => !prev)}
                >
                  <div className="flex items-center space-x-1">
                    <img
                      src="/icon_google_cloud.svg"
                      alt=""
                      className="h-4 w-4"
                    />
                    <span>
                      External Files {loadingExternal && " — Loading…"}
                    </span>
                  </div>
                  <ArrowIcon isOpen={externalOpen} />
                </div>
                {externalOpen &&
                  externalFiles.files.map((file) => {
                    const checked = isSelected(file.id);
                    return (
                      <div
                        key={`external-g-${file.id}`}
                        onClick={() => handleToggle(file)}
                        className={`${ROW_BASE_CLASSES} ${
                          checked ? "bg-[#F2F4F7]" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="mr-2 accent-blue-600"
                          checked={checked}
                          onChange={() => handleToggle(file)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <img
                          src={getFileIcon(file.filetype)}
                          alt=""
                          className="h-5 w-5 mr-2"
                        />
                        <p
                          className={
                            showOneLine
                              ? "text-ellipsis line-clamp-1"
                              : "break-all whitespace-normal"
                          }
                        >
                          {file.filename}
                        </p>
                      </div>
                    );
                  })}
              </>
            )}

            {/* Google Drive Folders */}
            {(loadingFolders || gdriveFolders.length > 0) && (
              <>
                {foldersOpen &&
                  gdriveFolders.map((folder) => (
                    <DriveFolderDropdownPreview
                      key={folder.id}
                      folder={folder}
                      projectId={selectedProject?.id || ""}
                      isSelected={isSelected}
                      onToggle={handleToggle}
                      showOneLine={showOneLine}
                      provider="google"
                      iconSrc="/icon_google_cloud.svg"
                      getFolderFiles={fetchFolderFiles}
                    />
                  ))}
              </>
            )}

            {/* OneDrive External Files */}
            {(loadingExternalFilesOneDrive ||
              externalFilesDataOneDrive.files.length > 0) && (
              <>
                <div
                  className={SECTION_HEADER_CLASSES}
                  onClick={() => setExternalOpenOneDrive((prev) => !prev)}
                >
                  <div className="flex items-center space-x-1">
                    <img
                      src="/icon_ms_onedrive.svg"
                      alt=""
                      className="h-4 w-4"
                    />
                    <span>
                      External Files
                      {loadingExternalFilesOneDrive && " — Loading…"}
                    </span>
                  </div>
                  <ArrowIcon isOpen={externalOpenOneDrive} />
                </div>
                {externalOpenOneDrive &&
                  externalFilesDataOneDrive.files.map((file) => {
                    const checked = isSelected(file.id);
                    return (
                      <div
                        key={`external-o-${file.id}`}
                        onClick={() => handleToggle(file)}
                        className={`${ROW_BASE_CLASSES} ${
                          checked ? "bg-[#F2F4F7]" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="mr-2 accent-blue-600"
                          checked={checked}
                          onChange={() => handleToggle(file)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <img
                          src={getFileIcon(file.filetype)}
                          alt=""
                          className="h-5 w-5 mr-2"
                        />
                        <p
                          className={
                            showOneLine
                              ? "text-ellipsis line-clamp-1"
                              : "break-all whitespace-normal"
                          }
                        >
                          {file.filename}
                        </p>
                      </div>
                    );
                  })}
              </>
            )}

            {/* OneDrive Folders */}
            {(loadingFoldersOneDrive || oneDriveFolders.length > 0) && (
              <>
                {foldersOpenOneDrive &&
                  oneDriveFolders.map((folder) => (
                    <DriveFolderDropdownPreview
                      key={folder.id}
                      folder={folder}
                      projectId={selectedProject?.id || ""}
                      isSelected={isSelected}
                      onToggle={handleToggle}
                      showOneLine={showOneLine}
                      provider="onedrive"
                      iconSrc="/icon_ms_onedrive.svg"
                      getFolderFiles={fetchOneDriveFolderFiles}
                    />
                  ))}
              </>
            )}

            {/* Empty State */}
            {!loadingRootView &&
              !loadingExternal &&
              !loadingExternalFilesOneDrive &&
              !loadingFolders &&
              !loadingFoldersOneDrive &&
              localFilesCount === 0 &&
              localFoldersCount === 0 &&
              !externalFiles.files.length &&
              !gdriveFolders.length &&
              !externalFilesDataOneDrive.files.length &&
              !oneDriveFolders.length && (
                <div className="px-3 py-4 text-xs 2xl:text-sm text-gray-500">
                  No files available.
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdownAssistant;
